import React, { useState } from 'react';

import { useParams } from 'react-router-dom';

import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';

import { GapRadarChart } from '@/components/assessment-engine/GapRadarChart';
import { ResultsComparisonLayout } from '@/components/assessment-engine/ResultsComparisonLayout';
import {
  formatScore,
  scaleSigma,
  scoreGapFromTarget,
} from '@/components/assessment-engine/resultsScoreUtils';
import { GapValue } from '@/components/assessment-engine/GapValue';

import {

  AE_BTN_PRIMARY,

  AE_CARD,

  AE_INPUT,

  AE_LABEL,

  AE_LINK,

  AE_MUTED,

  AE_PUBLIC_PAGE,

  AE_SECTION_TITLE,

  AE_TITLE,

} from '@/components/assessment-engine/layout/aeTheme';

import { assessmentEngineService } from '@/services/assessmentEngineService';

import type { AeLiveReport } from '@/types/assessmentEngine';



const SESSION_KEY = 'ae_report_session';



/** Public live report viewer — email OTP then session-backed live data. */

export const LiveReportPage: React.FC = () => {

  const { t } = useLanguage();

  const { shareToken } = useParams<{ shareToken: string }>();

  const [step, setStep] = useState<'email' | 'otp' | 'report'>('email');

  const [email, setEmail] = useState('');

  const [code, setCode] = useState('');

  const [sessionToken, setSessionToken] = useState<string | null>(null);

  const [report, setReport] = useState<AeLiveReport | null>(null);

  const [loading, setLoading] = useState(false);



  const storageKey = shareToken ? `${SESSION_KEY}:${shareToken}` : '';



  const loadReport = async (token: string, session: string) => {

    setLoading(true);

    try {

      const data = await assessmentEngineService.getLiveReport(token, session);

      setReport(data);

      setStep('report');

    } catch {

      toast.error('Could not load report — sign in again');

      localStorage.removeItem(storageKey);

      setStep('email');

    } finally {

      setLoading(false);

    }

  };



  React.useEffect(() => {

    if (!shareToken || !storageKey) return;

    const saved = localStorage.getItem(storageKey);

    if (saved) {

      setSessionToken(saved);

      void loadReport(shareToken, saved);

    }

  }, [shareToken]);



  const requestOtp = async () => {

    if (!shareToken || !email.trim()) return;

    setLoading(true);

    try {

      await assessmentEngineService.requestReportOtp(shareToken, email.trim());

      toast.success(t('assessmentEngine.take.otpSent'));

      setStep('otp');

    } catch {

      toast.error(t('assessmentEngine.take.otpSendFailed'));

    } finally {

      setLoading(false);

    }

  };



  const verifyOtp = async () => {

    if (!shareToken) return;

    setLoading(true);

    try {

      const { session_token } = await assessmentEngineService.verifyReportOtp(

        shareToken,

        email.trim(),

        code.trim()

      );

      localStorage.setItem(storageKey, session_token);

      setSessionToken(session_token);

      await loadReport(shareToken, session_token);

    } catch {

      toast.error('Invalid code');

    } finally {

      setLoading(false);

    }

  };



  if (!shareToken) {

    return (

      <div className={`${AE_PUBLIC_PAGE} p-6`}>

        <p className={AE_MUTED}>{t('assessmentEngine.live.invalidLink')}</p>

      </div>

    );

  }



  if (step !== 'report' || !report) {

    return (

      <div className={`${AE_PUBLIC_PAGE} flex items-center justify-center p-6`}>

        <div className={`${AE_CARD} max-w-md w-full space-y-4`}>

          <h1 className={AE_TITLE}>{t('assessmentEngine.live.title')}</h1>

          {step === 'email' && (

            <>

              <label className={AE_LABEL}>{t('assessmentEngine.live.email')}</label>

              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={AE_INPUT} />

              <button

                type="button"

                disabled={loading || !email.trim()}

                onClick={requestOtp}

                className={`w-full ${AE_BTN_PRIMARY}`}

              >

                {t('assessmentEngine.live.sendCode')}

              </button>

            </>

          )}

          {step === 'otp' && (

            <>

              <p className={AE_MUTED}>{t('assessmentEngine.live.enterCode', { email })}</p>

              <input

                type="text"

                inputMode="numeric"

                maxLength={6}

                value={code}

                onChange={(e) => setCode(e.target.value)}

                className={`${AE_INPUT} tracking-widest text-center`}

              />

              <button

                type="button"

                disabled={loading || code.length < 6}

                onClick={verifyOtp}

                className={`w-full ${AE_BTN_PRIMARY}`}

              >

                {t('assessmentEngine.live.verify')}

              </button>

            </>

          )}

          {loading && (

            <p className={`${AE_MUTED} text-center`}>{t('assessmentEngine.campaigns.loading')}</p>

          )}

        </div>

      </div>

    );

  }



  const b1 = report.benchmark?.b1;
  const narrative = report.narrative;
  const showB1 = b1?.cohort_met === true;
  return (

    <div className={`${AE_PUBLIC_PAGE} p-6`}>

      <div className="container mx-auto w-full space-y-6">

        <h1 className={AE_TITLE}>{t('assessmentEngine.live.title')}</h1>



        {narrative?.narrative_stale && (

          <div className="bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 text-[var(--color-warning)] text-sm rounded p-3">

            {t('assessmentEngine.live.staleBanner')}

          </div>

        )}



        {narrative?.display_text && (

          <div className={AE_CARD}>

            <h2 className={`${AE_SECTION_TITLE} mb-1`}>{t('assessmentEngine.live.summary')}</h2>

            <p className="text-xs text-[var(--color-secondary-text)] mb-2">

              {narrative.assistive_label ?? t('assessmentEngine.report.assistiveLabel')}

            </p>

            <p className="text-sm text-[var(--color-surface-text)] whitespace-pre-wrap">{narrative.display_text}</p>

          </div>

        )}



        <div className={AE_CARD}>

          <p className={`${AE_MUTED} mb-4`}>

            {report.complete_count} complete · {report.response_count} total · As of{' '}

            {new Date(report.as_of).toLocaleString()}

          </p>

          {b1 && !showB1 && (
            <p className="mb-4 text-sm text-[var(--color-warning)]">
              {t('assessmentEngine.report.benchmarkHidden')}
            </p>
          )}

          <ResultsComparisonLayout
            showChart={!!report.individual}
            chart={
              report.individual ? (
                <GapRadarChart
                  sections={report.individual.sections}
                  aggregateBySection={
                    showB1
                      ? Object.fromEntries(b1!.sections.map((s) => [s.section_id, s.mean_score]))
                      : undefined
                  }
                  yourScoreLabel={t('assessmentEngine.results.yourScore')}
                  aggregateLabel={t('assessmentEngine.report.benchmark')}
                />
              ) : null
            }
            table={
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-surface-text)]">
                      <th className="py-2">{t('assessmentEngine.report.section')}</th>
                      {report.individual && (
                        <th className="py-2">{t('assessmentEngine.results.yourScore')}</th>
                      )}
                      {showB1 && <th className="py-2">{t('assessmentEngine.report.benchmark')}</th>}
                      {showB1 && report.individual && (
                        <th className="py-2">{t('assessmentEngine.results.gapToBenchmark')}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {report.sections.map((sec) => {
                      const bench = b1?.sections.find((b) => b.section_id === sec.section_id);
                      const mine = report.individual?.sections.find((s) => s.section_id === sec.section_id);
                      return (
                        <tr key={sec.section_id} className="border-b border-[var(--color-border)]">
                          <td className="py-2 text-[var(--color-surface-text)]">{sec.section_name}</td>
                          {report.individual && (
                            <td className="py-2 text-[var(--color-surface-text)]">
                              {formatScore(mine?.mean_score)}
                            </td>
                          )}
                          {showB1 && (
                            <td className="py-2 text-[var(--color-surface-text)]">
                              {formatScore(bench?.mean_score)}
                            </td>
                          )}
                          {showB1 && report.individual && (
                            <td className="py-2">
                              <GapValue
                                gap={scoreGapFromTarget(
                                  mine?.mean_score ?? null,
                                  bench?.mean_score ?? null
                                )}
                                sigma={
                                  bench?.std_dev != null && bench.std_dev > 0
                                    ? bench.std_dev
                                    : scaleSigma(report.scale_max)
                                }
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-4 text-xs text-[var(--color-secondary-text)]">{report.scoring_footnote}</p>
              </>
            }
          />

        </div>



        {sessionToken && (

          <button

            type="button"

            className={AE_LINK}

            onClick={() => {

              localStorage.removeItem(storageKey);

              setReport(null);

              setStep('email');

              setSessionToken(null);

            }}

          >

            {t('assessmentEngine.live.signOut')}

          </button>

        )}

      </div>

    </div>

  );

};


